import { Link, useMatchRoute, useNavigate } from '@tanstack/react-router'
import { createContext, Suspense, useContext, useState } from 'react'

import NoInquiryImage from '@/assets/no_inquiry.svg'
import { ArrowForwardIcon, ChatBubbleIcon } from '@/components/icon'
import {
  Box,
  Button,
  Center,
  CounterInput,
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
  Grid,
  HStack,
  LinkBox,
  LinkOverlay,
  Skeleton,
  Stack,
  TabsContent,
  TabsList,
  TabsRoot,
  TabsTrigger,
  Tag,
  Text,
} from '@/components/ui'
import { useGqlQuerySuspense } from '@/lib/api'
import { FragmentType, getFragmentData, graphql } from '@/lib/gql'
import { InquiryLogStatus } from '@/lib/gql/graphql'
import { useNewInquiryContext } from '@/pages/TrialDetailPage/useNewInquiry'
import { formatDate, lastUpdatedLabel } from '@/utils/datetime'
import { getFullName } from '@/utils/getFullName'

type InquiryListTabContextType = {
  activeTab: string
  setActiveTab: (tab: string) => void
}

const InquiryListContext = createContext<InquiryListTabContextType | null>(null)

const useInquiryListTab = () => {
  const context = useContext(InquiryListContext)
  if (!context) {
    throw new Error(
      'useInquiryListTab must be used within a InquiryListTabProvider',
    )
  }
  return context
}

export const InquiryListProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [activeTab, setActiveTab] = useState<string>('open')

  return (
    <InquiryListContext.Provider value={{ activeTab, setActiveTab }}>
      {children}
    </InquiryListContext.Provider>
  )
}

const inquiryListItemInquiry = graphql(`
  fragment InquiryListInquiryItem on InquiriesPayloadElement {
    unread
    latestMessagePostedAt
    inquiry {
      inquiryUid
      inquiryNumber
      inquiryTitle
      trial {
        trialNumber
      }
      firstLog {
        author {
          ... on BusinessUser {
            organizationName
            generalUser {
              firstName
              lastName
            }
          }
        }
      }
    }
  }
`)
type InquiryListItemProps = {
  inquiry: FragmentType<typeof inquiryListItemInquiry>
}
const InquiryListItem = ({
  inquiry: inquiryFragment,
}: InquiryListItemProps) => {
  const { unread, latestMessagePostedAt, inquiry } = getFragmentData(
    inquiryListItemInquiry,
    inquiryFragment,
  )
  const postedTimeText = `${formatDate(latestMessagePostedAt, 'YYYY/MM/DD HH:mm')}(${lastUpdatedLabel(latestMessagePostedAt)})`
  const matchRoute = useMatchRoute()

  const isTrialDetailPage = !!matchRoute({ to: '/trials/$trialNumber' })

  return (
    <LinkBox
      as={HStack}
      p={4}
      gap={2}
      borderBottom="1px solid"
      borderColor="gray.300"
      bgColor="transparent"
      bgPos="center"
      transition="background 0.8s"
      css={{
        '--hover-bg-color': '{colors.blue.500/4}',
        '&:hover': {
          background:
            'var(--hover-bg-color) radial-gradient(circle, var(--hover-bg-color) 0.1%, transparent 0.1%) center/150000%',
        },
        '&:active': {
          bgColor: 'blue.500/8',
          backgroundSize: '100%',
          transition: 'background 0s',
        },
      }}
    >
      <Stack gap={1} w="full">
        {unread ? (
          <HStack gap={1}>
            <Tag colorPalette="red" variant="solid" size="sm">
              未読
            </Tag>
            <Text color="text.error" fontSize="xs">
              {postedTimeText}
            </Text>
          </HStack>
        ) : (
          <Text color="text.sub" fontSize="xs">
            {postedTimeText}
          </Text>
        )}
        <HStack gap={1}>
          <Text as="span" color="text.sub" fontWeight="bold">
            <LinkOverlay asChild>
              <Link
                to="/trials/$trialNumber"
                params={{
                  trialNumber: inquiry.trial.trialNumber,
                }}
                replace={isTrialDetailPage} // 詳細画面にいる場合はreplaceしてhistoryを残さない
                search={{
                  inquiry: inquiry.inquiryUid,
                }}
              >
                #{inquiry.inquiryNumber}
              </Link>
            </LinkOverlay>
          </Text>
          <Text color="text.sub" fontSize="sm">
            -
          </Text>
          <Text color="text.sub" fontSize="sm">
            {inquiry.trial.trialNumber}
          </Text>
        </HStack>
        <Text
          color="text.sub"
          fontSize="sm"
          fontWeight="bold"
          textOverflow="ellipsis"
        >
          {inquiry.inquiryTitle}
        </Text>
        <Text color="gray.700" wordBreak="break-all">
          {getFullName({
            firstName: inquiry.firstLog.author.generalUser.firstName,
            lastName: inquiry.firstLog.author.generalUser.lastName,
          })}
        </Text>
        <Text color="gray.700">{inquiry.firstLog.author.organizationName}</Text>
      </Stack>

      <Box minW="max-content">
        <ArrowForwardIcon color="var(--chakra-colors-gray-500)" />
      </Box>
    </LinkBox>
  )
}

type NewInquiryDialogProps = {
  trigger: React.ReactNode
}
const NewInquiryDialog = ({ trigger }: NewInquiryDialogProps) => {
  const { title, onChangeTitle, resetTitle, openInquiryDialog } =
    useNewInquiryContext()
  const navigate = useNavigate()
  return (
    <DialogRoot onInteractOutside={resetTitle}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>

      <DialogContent>
        <DialogHeader fontSize="lg" fontWeight="bold" color="gray.700">
          問い合わせタイトルの入力
        </DialogHeader>
        <DialogBody>
          <CounterInput
            placeholder="問い合わせタイトルを入力してください"
            value={title}
            onChange={e => {
              onChangeTitle(e.target.value)
            }}
            invalid={title.length > 20}
            errorText="タイトルは20文字以内で入力してください"
            label={<Text color="text.sub">問い合わせタイトル</Text>}
            maxLength={20}
          />
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="solid" colorPalette="gray" onClick={resetTitle}>
              キャンセル
            </Button>
          </DialogActionTrigger>
          <DialogActionTrigger asChild>
            <Button
              disabled={!title || title.length > 20}
              onClick={() => {
                navigate({
                  to: '.',
                  search: {
                    inquiry: undefined, // 別の問い合わせが開いている場合は閉じる
                  },
                })
                openInquiryDialog()
              }}
            >
              メッセージの入力へ進む
            </Button>
          </DialogActionTrigger>
        </DialogFooter>
        <DialogCloseTrigger onClick={resetTitle} />
      </DialogContent>
    </DialogRoot>
  )
}

const NoInquiry = ({ showActionButton }: { showActionButton: boolean }) => {
  return (
    <Center flex={1} h="full" w="full">
      <Stack gap={4} alignItems="center">
        <Stack gap={2} alignItems="center">
          <NoInquiryImage />
          <Text color="blue.400">問い合わせはありません</Text>
        </Stack>
        {showActionButton && (
          <NewInquiryDialog
            trigger={<Button size="lg">問い合わせを開始する</Button>}
          />
        )}
      </Stack>
    </Center>
  )
}

const NoCompletedInquiry = () => {
  return (
    <Center flex={1} h="full" w="full">
      <Stack gap={2} alignItems="center">
        <NoInquiryImage />
        <Text color="blue.400">完了した問い合わせはありません</Text>
      </Stack>
    </Center>
  )
}

const inquiryElementsFragment = graphql(`
  fragment InquiryElement on InquiriesPayloadElement {
    inquiry {
      inquiryUid
    }
    ...InquiryListInquiryItem
  }
`)
type OpeningInquiryListProps = {
  showActionButton: boolean
  openingInquiries: FragmentType<typeof inquiryElementsFragment>[]
}
const OpeningInquiryList = ({
  showActionButton,
  openingInquiries,
}: OpeningInquiryListProps) => {
  const elements = getFragmentData(inquiryElementsFragment, openingInquiries)
  if (elements.length === 0) {
    return <NoInquiry showActionButton={showActionButton} />
  }

  return elements.map(element => (
    <InquiryListItem key={element.inquiry.inquiryUid} inquiry={element} />
  ))
}

type CompletedInquiryListProps = {
  completedInquiries: FragmentType<typeof inquiryElementsFragment>[]
}
const CompletedInquiryList = ({
  completedInquiries,
}: CompletedInquiryListProps) => {
  const elements = getFragmentData(inquiryElementsFragment, completedInquiries)
  if (elements.length === 0) {
    return <NoCompletedInquiry />
  }

  return elements.map(element => (
    <InquiryListItem key={element.inquiry.inquiryUid} inquiry={element} />
  ))
}

const getInquiriesQuery = graphql(`
  query GetInquiries($trialUid: UUID) {
    # ページング未対応なので十分に大きい数を取得するようにする
    inquiries(input: { trialUid: $trialUid, filterStatus: All, per: 1000 }) {
      elements {
        __typename
        inquiry {
          latestLog {
            status
          }
        }
        ...InquiryElement
      }
    }
  }
`)

type InquiryListContentProps = {
  listTitle: string
  showActionButton?: boolean
  trialUid?: string
}
const InquiryListContent = ({
  listTitle,
  showActionButton = false,
  trialUid,
}: InquiryListContentProps) => {
  const {
    data: { inquiries },
  } = useGqlQuerySuspense(
    {
      document: getInquiriesQuery,
      variable: {
        trialUid,
      },
    },
    {
      refreshInterval: 30000,
    },
  )

  const { activeTab, setActiveTab } = useInquiryListTab()

  const openingInquiries = inquiries.elements.filter(e => {
    return e.inquiry.latestLog.status === InquiryLogStatus.Opening
  })
  const completedInquiries = inquiries.elements.filter(e => {
    return e.inquiry.latestLog.status === InquiryLogStatus.Completed
  })

  return inquiries.elements.length === 0 ? (
    <Grid gap={0} h="full" templateRows="auto 1fr" bgColor="white">
      <Box px={4} py={3}>
        <Text color="text.sub" fontWeight="semibold">
          {listTitle}
        </Text>
      </Box>

      <NoInquiry showActionButton={showActionButton} />
    </Grid>
  ) : (
    <Grid
      gap={0}
      h="full"
      templateRows={
        showActionButton && openingInquiries.length > 0
          ? 'auto auto 1fr'
          : 'auto 1fr'
      }
      bgColor="white"
    >
      <Box px={4} py={3}>
        <Text color="text.sub" fontWeight="semibold">
          {listTitle}
        </Text>
      </Box>

      {showActionButton && openingInquiries.length > 0 && (
        <Box px={4} py={2}>
          <NewInquiryDialog
            trigger={
              <Button size="lg" leftIcon={<ChatBubbleIcon />}>
                新しい問い合わせを開始する
              </Button>
            }
          />
        </Box>
      )}

      <TabsRoot
        h="full"
        minH="320px"
        variant="line"
        defaultValue={activeTab}
        display="flex"
        flexDirection="column"
        overflow="hidden"
        onValueChange={e => setActiveTab(e.value)}
      >
        <TabsList w="full">
          <TabsTrigger
            w="50%"
            justifyContent="center"
            variant="line"
            value="open"
          >
            対応中
          </TabsTrigger>
          <TabsTrigger
            w="50%"
            justifyContent="center"
            variant="line"
            value="complete"
          >
            完了
          </TabsTrigger>
        </TabsList>

        <TabsContent h="full" value="open" pt={0} flex={1} overflow="auto">
          <OpeningInquiryList
            showActionButton={showActionButton}
            openingInquiries={openingInquiries}
          />
        </TabsContent>
        <TabsContent h="full" value="complete" pt={0} overflow="auto">
          <CompletedInquiryList completedInquiries={completedInquiries} />
        </TabsContent>
      </TabsRoot>
    </Grid>
  )
}

const InquiryListFallback = () => {
  return (
    <Center boxSize="full">
      <Stack gap="4">
        {[...Array(3)].map((_, index) => (
          <Skeleton key={index} />
        ))}
      </Stack>
    </Center>
  )
}

type Props = InquiryListContentProps

export const InquiryList = (props: Props) => {
  return (
    <Suspense fallback={<InquiryListFallback />}>
      <InquiryListContent {...props} />
    </Suspense>
  )
}
